import React from 'react';
import { useSelector } from 'react-redux';
import { ButtonShapes } from '@mlb/react-patterns/v4/components/Button/button.types';
import Button from '@mlb/react-patterns/v4/components/Button/button';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import { getContentMediaItem, getMediaItem, MEDIA_ARCHIVE, MEDIA_ON } from './getMediaState';
import { getBroadcastsConfigState } from '../store/selectors/broadcastsConfigSelector';
import { getDeepLinkConfigState } from '../store/selectors/getDeepLinkConfigSelector';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import getTranslation from './getTranslation';
import getAffiliateIdUrl from './getAffiliateIdUrl';
import COLORS from '@mlb/react-patterns/v4/style/universal.color';
import { cardTypes, buttonDisplayFormatConfig } from './productButtonConfigs';
import styled from 'styled-components';
import getAffiliateId from '../store/selectors/getAffiliateId';
import getForceWatchLinks from '../store/selectors/getForceWatchLinks';
import getUrlRoot, { getSanitizedRoot } from './getUrlRoot';
import { token } from '@mlb/react-patterns/styles';
import useMakeTFL from '../hooks/useMakeTFL';

const {
    mlbGrey5
} = COLORS;

export const ButtonComponent = styled(Button)``;
export const ButtonLink = styled(Link)``;
export const HyperLink = styled(Link)`
      display: flex;
      &:focus-visible:not(:disabled), &:active:not(:disabled) {
        background-color: ${token('state/pressed')};
        outline: 2px solid ${token('border/inverse')};
        outline-offset: -2px;
        box-shadow: none;
      }   
`;

// Simple wrapper for overriding color and font size
export const ButtonWrapper = styled.div`
    ${ButtonLink} {
        ${ButtonComponent} {
            span {
                ${({ color }) => color ? `color: ${color};` : ''}
                font-size: ${({ fontSize }) => fontSize};
            }
        }
    }
    display: inline-block;
`;

// Generates an array of product buttons that can have any assortment of
// labels and/or icons, colors, urls, etc.
export const generateProductButtons = ({
    cardType,
    isMobile = false,
    buttonsArr,
    dictionary,
    gamePk,
    urlRoot,
    ticketsLink,
    ticketLinkClassName = 'bdata-react-sns-tickets-link',
    isLoading,
    mediaEpg,
    statusCode,
    abstractGameCode,
    isPostponed,
    gamedayType,
    isCancelled,
    iconSize,
    gameDate,
    handlersMap,
    storyLink,
    awayTeamName,
    homeTeamName,
    broadcasts,
    content,
    iconColor = mlbGrey5,
    locale,
    children,
    linkProps,
    componentName
}) => {
    // Retrieve the button display formatting
    const displayFormatConfig = buttonDisplayFormatConfig[cardType];
    const isMiniSB = cardType === cardTypes.MINI_SB;
    const isSchedule = cardType === cardTypes.SCHEDULE;
    const isMIG = cardType === cardTypes.MIG;
    const { affiliateIdFromConfig, shouldShowAffiliateId } = useSelector(getAffiliateId);
    const makeTFL = useMakeTFL();

    const {
        showIcons,
        showLabel,
        buttonStyle,
        buttonLimit = 4,
        affiliateId: affiliateIdDefault
    } = displayFormatConfig;

    const affiliateId = affiliateIdFromConfig || affiliateIdDefault;

    const statusCodeFirstChar = (statusCode && statusCode[0]) || '';
    let hasButtons = false;

    const broadcastConfig = useSelector(getBroadcastsConfigState) || {};
    const { useContentEpg } = broadcastConfig;
    const mediaItem = useContentEpg ? getContentMediaItem(content) : getMediaItem(broadcasts);
    const mediaState = useContentEpg ? mediaItem?.mediaState : mediaItem?.mediaState?.mediaStateCode;

    const { isApp, deepLinkPrefix } = useSelector(getDeepLinkConfigState);
    const buttons = [];
    const links = [];
    let baseURL = urlRoot;

    const forceWatchLinks = useSelector(getForceWatchLinks);

    buttonsArr?.forEach((buttonProps) => {
        const buttonsLength = buttons.length;

        // If we reach the button limit, we're done.
        if (buttonsLength >= buttonLimit) {
            return;
        }
        let {
            label,
            style,
            url,
            icon,
            target,
            id
        } = buttonProps;

        // Remove icon retrieved if icons aren't to be shown
        if (!showIcons) {
            icon = undefined;
        }
        let buttonComponent = null;
        let linkComponent = null;
        let linkUrl;
        let ariaLabel;
        let linkClassName = '';
        let handler;

        switch (true) {
            case id === 'gameday':
                // Only display on certain gameday types
                if (gamedayType === 'L' || gamedayType === 'S') {
                    return;
                }
                // Only show preview-state gameday buttons if the status code isn't "S" (Scheduled)
                if (abstractGameCode === 'P' && statusCode === 'S') {
                    return;
                }

                if (gamedayType === 'N') {
                    const { liveBoxButton } = buttonProps;
                    icon = showIcons ? liveBoxButton.icon : undefined;
                    url = getAffiliateIdUrl(liveBoxButton.url, affiliateId, shouldShowAffiliateId);
                    label = liveBoxButton.label;
                    target = liveBoxButton.target;
                }
                baseURL = urlRoot || getUrlRoot(locale, true);

                break;
            case id === 'tickets':
            // if no tickets link then do not display tickets button
                if (!ticketsLink) {
                    return;
                }

                linkUrl = makeTFL(getAffiliateIdUrl(url(ticketsLink), affiliateId, shouldShowAffiliateId), componentName);

                if (isSchedule) {
                    const { scheduleIcon } = buttonProps;
                    icon = scheduleIcon;
                }
                // Add this class to the link for section-level tracking with TFL
                linkClassName = ticketLinkClassName;
                break;
            case (id === 'watchPreGameShow' || id === 'watch' || id === 'watchMilb'):
                if (locale === 'ja') {
                    return;
                }

                // if we're processing a PreGameShow watch button
                // and the preGameShow hasn't started yet, return no button
                // as we don't want to send users to a blank slate.
                if (id === 'watchPreGameShow') {
                    if (!mediaItem?.hasPreGameShow || (mediaItem?.hasPreGameShow && !mediaItem?.preGameShowStarted)) {
                        return;
                    }
                }
                if (forceWatchLinks || mediaState === MEDIA_ARCHIVE || mediaState === MEDIA_ON) {
                    label = buttonProps.label; // re-assert
                    linkUrl = getAffiliateIdUrl(url(urlRoot, gamePk, gameDate, { isApp, deepLinkPrefix }), affiliateId, true);
                    handler = (forceWatchLinks || mediaState !== MEDIA_ARCHIVE) ? handlersMap?.watch : undefined;
                    if (mediaState === MEDIA_ON) {
                        iconColor = mlbGrey5;
                    }
                } else {
                    return;
                }
                break;
            case (id === 'youTube' || id === 'appleTv' || id === 'partner'): {
                const { watchButton } = buttonProps;
                icon = showIcons ? (buttonProps?.icon || watchButton.icon) : undefined;
                handler = (forceWatchLinks || mediaState !== MEDIA_ARCHIVE) ? handlersMap?.watch : undefined;
                if (id === 'partner') {
                    linkClassName = 'button_partner';
                }
                if (mediaState === MEDIA_ARCHIVE && !(isSchedule && isMobile)) {
                    label = (mediaState === MEDIA_ARCHIVE) ? watchButton.label : buttonProps?.label;
                    iconColor = mlbGrey5;
                    linkUrl = getAffiliateIdUrl(watchButton.url(urlRoot, gamePk, gameDate), affiliateId, shouldShowAffiliateId);
                } else if (forceWatchLinks || mediaState === MEDIA_ON) {
                    if (isMIG) {
                        label = buttonProps.label;
                    }

                    if (id === 'youTube' || id === 'appleTv') {
                        linkUrl = getAffiliateIdUrl(buttonProps.url(mediaEpg[id.toLowerCase()]?.url), affiliateId, shouldShowAffiliateId);
                    }
                } else {
                    return;
                }
                break;
            } case id === 'highlights':
            // TODO add some logic for adding a tag to button for number of highlights
            // Logic is TBD currently
                if (abstractGameCode === 'F' && (statusCodeFirstChar === 'C' || statusCodeFirstChar === 'D')) {
                    return;
                }

                if (isMiniSB) {
                    label = 'minisb_button_highlights';
                }
                break;
            case id === 'wrap':
            // Display the preview link if the game is final and postponed or cancelled
                if (abstractGameCode === 'F' && (isPostponed || isCancelled)) {
                    return;
                }

                baseURL = urlRoot || getUrlRoot(locale, true);
                break;
            case id === 'preview':
                if (abstractGameCode === 'P' && statusCode !== 'S') {
                    return;
                }
                // Display the preview link if the game is final and postponed or cancelled
                if (abstractGameCode === 'F' && !isPostponed && !isCancelled) {
                    return;
                }

                if (isPostponed || isCancelled) {
                    label = 'details';
                }

                baseURL = urlRoot || getUrlRoot(locale, true);
                break;
            case id === 'box':
            // Do not display based if certain statusCodes
                if (statusCodeFirstChar === 'C' || statusCodeFirstChar === 'D') {
                    return;
                }
                break;
            case id === 'story':
                if (!storyLink) {
                    return;
                }
                linkUrl = getAffiliateIdUrl(buttonProps.url(storyLink), affiliateId, shouldShowAffiliateId);
                break;
            default:
                return;
        }

        if (isLoading && showLabel) {
            buttonComponent = <Skeleton height={ 18 } width={ 60 } key={ `skeleton-${buttonsLength}` } margin='0px' />;
        } else if (isLoading && cardType === cardTypes.MIG) {
            hasButtons = true;
            buttonComponent = <Skeleton height={ 20 } width={ 20 } key={ `skeleton-${buttonsLength}` } margin='0px' />;
        } else {
            const translatedLabel = getTranslation(dictionary, label, label);
            const gameContextLabel = getTranslation(dictionary, 'reader_label_game_context', '{{awayTeamName}} at {{homeTeamName}}', { homeTeamName: homeTeamName || 'home team', awayTeamName: awayTeamName || 'away team' });
            const defaultAriaLabel = [gameContextLabel, translatedLabel].join(' ');

            hasButtons = true;

            let fontSize = '13px';
            let fontColor;
            if (isMiniSB || isMIG) {
                fontSize = '11px';
                fontColor = iconColor;
            }

            const size = iconSize || (showIcons ? 24 : 15);

            buttonComponent = getProductLink(
                label,
                linkClassName,
                linkUrl ?? getAffiliateIdUrl((typeof url === 'function' && url(baseURL, gamePk, ticketsLink)), affiliateId, shouldShowAffiliateId) ?? '',
                `label-${buttons.length}`,
                icon,
                ariaLabel,
                translatedLabel,
                defaultAriaLabel,
                iconColor,
                style,
                size,
                showLabel,
                buttonStyle,
                fontSize,
                fontColor,
                target,
                handler
            );

            linkComponent = getProductClickThrough({
                label,
                linkClassName,
                linkUrl: linkUrl ?? getAffiliateIdUrl((typeof url === 'function' && url(baseURL, gamePk, ticketsLink)), affiliateId, shouldShowAffiliateId) ?? '',
                target,
                handler,
                children,
                linkProps
            });
        }

        if (buttonComponent) {
            buttons.push(buttonComponent);
        }

        if (linkComponent) {
            links.push(linkComponent);
        }
    });

    return { hasButtons, buttons, links };
};

// Builds a product link button that links to another page
const getProductLink = (
    label = '',
    linkClassName,
    linkUrl,
    key,
    icon,
    ariaLabel,
    translatedLabel,
    defaultAriaLabel,
    iconColor,
    style,
    iconSize = 15,
    showLabel,
    buttonStyle,
    fontSize = '13px',
    fontColor,
    target,
    handler,
    children
) => {
    const linkClass = `trk-${label.replace('.', '')} ${linkClassName}`;
    const buttonLabel = showLabel ? translatedLabel : undefined;
    const sanitizedLinkUrl = getSanitizedRoot(linkUrl);

    return (
        <ButtonWrapper
            key={ key }
            color={ fontColor }
            fontSize={ fontSize }
        >
            <ButtonLink
                className={ linkClass }
                url={ sanitizedLinkUrl }
                dataAttr={{ 'data-mlb-test': `productlink-${label}`, tabIndex: '-1' }}
                target={ target }
                handlers={handler}
            >
                <ButtonComponent
                    iconName={ icon }
                    iconLeft
                    shape={ ButtonShapes.HOLLOW_BORDERLESS }
                    label={ buttonLabel }
                    ariaLabel={ ariaLabel || defaultAriaLabel }
                    styleName={ style || buttonStyle }
                    hasHover={ false }
                    iconColor={ iconColor }
                    iconSize={ iconSize }
                    role={'link'}
                />
            </ButtonLink>
        </ButtonWrapper>
    );
};

// Builds a product link that links to another page
const getProductClickThrough = ({
    label = '',
    linkClassName,
    linkUrl,
    target,
    handler,
    children,
    linkProps = {}
}) => {
    const linkClass = `trk-${label.replace('.', '')} ${linkClassName}`;
    const sanitizedLinkUrl = getSanitizedRoot(linkUrl);
    const dataAttr = { ...linkProps, 'data-mlb-test': `productlink-${label}` };

    return (
        <HyperLink
            className={ linkClass }
            url={ sanitizedLinkUrl }
            dataAttr={dataAttr}
            handlers={handler}
            target={target}
            {...linkProps}
        >{children}</HyperLink>
    );
};

export default getProductLink;
